import { Box, Typography } from "@mui/material";
import FeedingIcon from "common/icons/FeedingIcon";
import FertiliserIcon from "common/icons/FertiliserIcon";
import HarvestIcon from "common/icons/HarvestIcon";
import MeasurementIcon from "common/icons/MeasurementIcon";
import OtherEventIcon from "common/icons/OtherEventIcon";
import PurchaseIcon from "common/icons/PurchaseIcon";
import SeedingIcon from "common/icons/SeedingIcon";
import { useTranslation } from "common/locales";
import React from "react";

interface RadialMenuProps {
  open: boolean;
  onClose: () => void;
  onEventTypeClick: (eventType: string) => void;
}

interface MenuItemConfig {
  id: string;
  labelKey: string;
  icon: React.ReactNode;
}

// Using #1C4321 to match the bottom navigation active color
const iconColor = "#1C4321";

const reseed: MenuItemConfig = {
  id: "reseed",
  labelKey: "addNewEvent.type.seeding",
  icon: <SeedingIcon fontSize="small" htmlColor={iconColor} />,
};
const fertilizer: MenuItemConfig = {
  id: "fertilizer",
  labelKey: "addNewEvent.type.fertilizer",
  icon: <FertiliserIcon fontSize="small" htmlColor={iconColor} />,
};
const yieldItem: MenuItemConfig = {
  id: "yield",
  labelKey: "addNewEvent.type.harvest",
  icon: <HarvestIcon fontSize="small" htmlColor={iconColor} />,
};
const cover: MenuItemConfig = {
  id: "cover",
  labelKey: "addNewEvent.type.measurement",
  icon: <MeasurementIcon fontSize="small" htmlColor={iconColor} />,
};
const feed: MenuItemConfig = {
  id: "feed",
  labelKey: "addNewEvent.type.feeding",
  icon: <FeedingIcon fontSize="small" htmlColor={iconColor} />,
};
const feedPurchase: MenuItemConfig = {
  id: "feedPurchase",
  labelKey: "addNewEvent.type.purchase",
  icon: <PurchaseIcon fontSize="small" htmlColor={iconColor} />,
};
const other: MenuItemConfig = {
  id: "other",
  labelKey: "addNewEvent.type.other",
  icon: <OtherEventIcon fontSize="small" htmlColor={iconColor} />,
};

// Rows in bottom-to-top (DOM) order — rendered with flexDirection: "column-reverse"
// so the first row here ends up flanking the FAB, tapering to a single item on top.
const rows: MenuItemConfig[][] = [
  [feedPurchase, other],
  [cover, feed],
  [fertilizer, yieldItem],
  [reseed],
];

// Gap within each row, matched 1:1 to `rows` above — tapers from wide
// (flanking the FAB) to narrow as the pyramid rises.
const rowGaps = ["76px", "40px", "16px", "0px"];

export default function RadialMenu({
  open,
  onClose,
  onEventTypeClick,
}: RadialMenuProps) {
  const { t } = useTranslation();

  const handleItemClick = (eventType: string) => {
    onEventTypeClick(eventType);
    onClose();
  };

  let itemIndex = 0;

  return (
    <>
      {/* Backdrop overlay */}
      <Box
        onClick={onClose}
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(128, 128, 128, 0.7)",
          zIndex: 9998,
          opacity: open ? 1 : 0,
          pointerEvents: open ? "auto" : "none",
          transition: "opacity 0.3s ease",
        }}
      />
      {/* Menu panel - a staggered pyramid of rows flanking the FAB and tapering to a
          single item on top. Each row is a plain flex row, so items can never overlap
          and stay evenly spaced within a row, regardless of item count. */}
      <Box
        sx={{
          position: "fixed",
          // BottomTabNavigation.tsx's Paper has minHeight: "70px" (line 389); clear
          // that plus a margin so the bottom row doesn't sit on top of its labels.
          bottom: "calc(80px + env(safe-area-inset-bottom))",
          left: "50%",
          transform: open
            ? "translate(-50%, 0) scale(1)"
            : "translate(-50%, 12px) scale(0.9)",
          transformOrigin: "bottom center",
          opacity: open ? 1 : 0,
          transition: "transform 0.25s ease, opacity 0.2s ease",
          zIndex: 9999,
          pointerEvents: open ? "auto" : "none",
          display: "flex",
          flexDirection: "column-reverse",
          alignItems: "center",
          gap: "10px",
        }}
      >
        {rows.map((row, rowIndex) => (
          <Box
            key={rowIndex}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              // Taper the gap row by row so the pyramid narrows toward the
              // top instead of looking like a rigid grid: widest at the
              // bottom (flanking the 62px FAB), narrowing as it goes up.
              gap: rowGaps[rowIndex],
            }}
          >
            {row.map((item) => {
              const delay = itemIndex * 0.03;
              itemIndex += 1;
              return (
                <Box
                  key={item.id}
                  onClick={() => open && handleItemClick(item.id)}
                  sx={{
                    width: "min(38vw, 155px)",
                    minHeight: "44px",
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    backgroundColor: "#ffffff",
                    border: "1px solid #e4e4e7",
                    borderRadius: "8px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    padding: "10px 16px",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    WebkitTapHighlightColor: "transparent",
                    opacity: open ? 1 : 0,
                    transition: `opacity 0.2s ease ${delay}s`,
                    "&:active": {
                      transform: "scale(0.96)",
                    },
                  }}
                >
                  {item.icon}
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 500,
                      color: "#09090b",
                      lineHeight: "20px",
                    }}
                  >
                    {t(item.labelKey)}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>
    </>
  );
}
